import { Fragment, Slice } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";

import { writeSliceToClipboard } from "@/components/tiptap/copy-paste/writeSliceToClipboard";

import type { DocumentModel } from "@/models/DocumentModel";
import type { Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";

type Line = { node: Node; pos: number; view: EditorView };

function resolveLine(editor: Editor | undefined, pos: number | undefined): Line | null {
  if (!editor || pos === undefined || pos > editor.state.doc.content.size) {
    return null;
  }

  const node = editor.state.doc.nodeAt(pos);

  return node ? { node, pos, view: editor.view } : null;
}

function writeLineToClipboard({ node, view }: Line): Promise<boolean> {
  return writeSliceToClipboard(view, new Slice(Fragment.from(node), 0, 0));
}

export async function copyLineAtPos(editor: Editor | undefined, pos: number | undefined) {
  const line = resolveLine(editor, pos);

  if (line) {
    await writeLineToClipboard(line);
  }
}

export async function cutLineAtPos(
  doc: DocumentModel,
  editor: Editor | undefined,
  getPos: () => number | undefined,
) {
  const line = resolveLine(editor, getPos());

  if (!line || !(await writeLineToClipboard(line))) {
    return;
  }

  const current = resolveLine(editor, getPos());

  if (!current || !editor) {
    return;
  }

  doc.transact(() => {
    editor
      .chain()
      .deleteRange({ from: current.pos, to: current.pos + current.node.nodeSize })
      .run();
  }, "line:cut");

  const { doc: pmDoc } = editor.state;
  const $pos = pmDoc.resolve(Math.min(current.pos, pmDoc.content.size));

  editor.chain().setTextSelection(TextSelection.near($pos, 1).from).focus().run();
}
